import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Field, Formik, Form } from "formik";
import { Trans, useTranslation } from "react-i18next";
import useProductName from "brand-customization/useProductName";
import i18n from "i18n";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import { Alert, ControlLabel, FormGroup, Col, Input, Row, SegmentedControl } from "components/bootstrap";
import {
  Select,
  Spinner,
  ReadOnlyFormGroup,
  ConfirmDialog,
  FormSubmit,
  FormikInput,
  InputDescription
} from "components/common";
import NumberUtils from "util/NumberUtils";
import DocumentationLink from "components/support/DocumentationLink";
import AppConfig from "util/AppConfig";
import ChecksumTypes from "archive/logic/ChecksumTypes";
import CompressionTypes from "archive/logic/CompressionTypes";
import BackendTypes from "archive/logic/BackendTypes";
import ArchiveConfigStreamSelectionForm from "archive/components/ArchiveConfigStreamSelectionForm";
import useArchiveConfiguration, {
  saveArchiveConfiguration,
  useInvalidateArchiveConfiguration
} from "archive/hooks/useArchiveConfiguration";
import { useArchiveBackends } from "archive/hooks/useArchiveBackends";
import useSnapshotArchivingTypes from "archive/hooks/useSnapshotArchivingTypes";
import { SEGMENT_COMPRESSIONS, SEGMENT_CHECKSUMS } from "archive/types";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import UserNotification from "util/UserNotification";
import usePermissions from "hooks/usePermissions";
import useDataTiering from "data-tiering/hooks/useDataTiering";
import useRepositories from "data-tiering/repository-management/hooks/useRepositories";
import SnapshotArchivingFields, {
  DEFAULT_SNAPSHOT_CONFIG,
  validateSnapshotArchivingFields
} from "./snapshot/SnapshotArchivingFields";
import ArchiveRoutes from "../Routes";
const StyledForm = styled(Form)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.md};
  `
);
const StyledAlert = styled(Alert)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.xs};
  `
);
const buildNestedErrors = (backendErrors) => {
  const result = {};
  Object.entries(backendErrors).forEach(([path, msgs]) => {
    var _a;
    const message = msgs.join(" ");
    const dotIndex = path.indexOf(".");
    if (dotIndex === -1) {
      result[path] = message;
    } else {
      const parent = path.slice(0, dotIndex);
      const child = path.slice(dotIndex + 1);
      (_a = result[parent]) != null ? _a : result[parent] = {};
      result[parent][child] = message;
    }
  });
  return result;
};
const validateArchiveConfigForm = (values) => {
  const errors = {};
  const tValidation = (key) => i18n.t(`archive_config_form.validation.${key}`, { ns: "enterprise_archive" });
  if (!values.restrict_to_leader && !(values.parallel_restore_jobs > 0)) {
    errors.parallel_restore_jobs = tValidation("min_restore_jobs");
  }
  if (!(values.restore_index_batch_size > 0)) {
    errors.restore_index_batch_size = tValidation("at_least_one");
  }
  if (!(values.max_segment_size > 0)) {
    errors.max_segment_size = tValidation("at_least_one");
  }
  if (!(values.retention_time >= 0)) {
    errors.retention_time = tValidation("at_least_zero");
  }
  if (!(values.archive_failure_threshold > 0)) {
    errors.archive_failure_threshold = tValidation("at_least_one");
  }
  if (!SEGMENT_COMPRESSIONS.includes(values.segment_compression_type)) {
    errors.segment_compression_type = tValidation("invalid_compression");
  }
  if (!SEGMENT_CHECKSUMS.includes(values.segment_checksum_type)) {
    errors.segment_checksum_type = tValidation("invalid_checksum");
  }
  const requiredFields = [
    "backend_id",
    "max_segment_size",
    "restore_index_batch_size",
    "archive_failure_threshold",
    "retention_time"
  ];
  requiredFields.forEach((field) => {
    if (values[field] === "" || typeof values[field] === "undefined") {
      errors[field] = tValidation("required");
    }
  });
  return errors;
};
const PARALLEL_RESTORE_JOBS_DEFAULT = 1;
const ARCHIVE_CONFIG_UPDATE_PERMISSION = "archiveconfig:update";
const ArchiveConfigForm = () => {
  var _a, _b, _c;
  const { t } = useTranslation("enterprise_archive");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [retentionTimeChanged, setRetentionTimeChanged] = useState(false);
  const history = useHistory();
  const sendTelemetry = useSendTelemetry("archive-configuration");
  const invalidateConfiguration = useInvalidateArchiveConfiguration();
  const isCloud = AppConfig.isCloud();
  const isSnapshotEnabled = !isCloud;
  const [userSelectedMode, setUserSelectedMode] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const valuesAtErrorRef = useRef(null);
  const [repositoriesUpdatedAtError, setRepositoriesUpdatedAtError] = useState(null);
  const formikRef = useRef(null);
  const { readiness } = useDataTiering(isSnapshotEnabled);
  const isVersionSupported = (_b = (_a = readiness.data) == null ? void 0 : _a.details.supported_by_search_version) != null ? _b : true;
  const { isPermitted } = usePermissions();
  const productName = useProductName();
  const canEdit = isPermitted("archiveconfig:update");
  const { repositories: allRepositories } = useRepositories(void 0, { enabled: isSnapshotEnabled });
  const { data: snapshotArchivingTypes } = useSnapshotArchivingTypes();
  const { data: initialArchive } = useArchiveConfiguration();
  const { data: backends } = useArchiveBackends(1, 1e3);
  const repositoryEntries = (_c = allRepositories.data) != null ? _c : [];
  const hasUnknownRepositorySignals = repositoryEntries.some(
    (entry) => entry.status === "unknown" || entry.credential_status === "unknown"
  );
  const checksOutstanding = !allRepositories.checksExhausted && hasUnknownRepositorySignals;
  const repositoriesResolvedSinceError = repositoriesUpdatedAtError !== null && allRepositories.dataUpdatedAt !== repositoriesUpdatedAtError && !hasUnknownRepositorySignals;
  useEffect(() => {
    var _a2;
    if (repositoriesResolvedSinceError) {
      (_a2 = formikRef.current) == null ? void 0 : _a2.validateForm();
    }
  }, [repositoriesResolvedSinceError]);
  if (!initialArchive || !backends) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const activeMode = isSnapshotEnabled ? userSelectedMode != null ? userSelectedMode : initialArchive.snapshot_archiving_config != null ? "snapshot" : "default" : "default";
  const supportedRepositoryTypes = (snapshotArchivingTypes != null ? snapshotArchivingTypes : []).map((t2) => t2.repository_type);
  const supportedRepositories = repositoryEntries.filter(
    (entry) => supportedRepositoryTypes.includes(entry.repository.type)
  );
  const credentialProblemRepositories = repositoryEntries.filter((entry) => entry.credential_status === "missing" || entry.credential_status === "invalid").map((entry) => entry.repository.name);
  const snapshotBlocked = (values) => {
    if (!isSnapshotEnabled || isCloud || activeMode !== "snapshot") {
      return false;
    }
    if (allRepositories.isLoading || checksOutstanding) {
      return true;
    }
    const selectedRepository = repositoryEntries.find(
      (entry) => {
        var _a2;
        return entry.repository.name === ((_a2 = values.snapshot_archiving_config) == null ? void 0 : _a2.repository_name);
      }
    );
    return supportedRepositories.length === 0 || credentialProblemRepositories.length > 0 || (selectedRepository == null ? void 0 : selectedRepository.status) === "not_ready";
  };
  const handleRestrictToLeaderChange = (setFieldValue, values, setFieldTouched) => {
    const newValue = !values.restrict_to_leader;
    setFieldValue("restrict_to_leader", newValue);
    if (newValue === true) {
      setFieldValue("parallel_restore_jobs", PARALLEL_RESTORE_JOBS_DEFAULT);
    }
    setFieldTouched("parallel_restore_jobs");
  };
  const updateConfiguration = (values, setErrors, setSubmitting, setFieldTouched) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.CONFIGURATION_UPDATED, {
      app_pathname: "archive"
    });
    const payload = isSnapshotEnabled && activeMode === "snapshot" ? values : { ...values, snapshot_archiving_config: null };
    saveArchiveConfiguration(payload).then(() => {
      invalidateConfiguration();
      setServerErrors({});
      history.replace(ArchiveRoutes.LIST);
    }).catch((error) => {
      var _a2, _b2;
      const body = (_a2 = error == null ? void 0 : error.additional) == null ? void 0 : _a2.body;
      if ((error == null ? void 0 : error.status) === 409) {
        UserNotification.error(
          t("archive_config_form.conflict_notification"),
          t("archive_config_form.update_failed_title")
        );
      } else if (typeof (body == null ? void 0 : body.errors) === "object") {
        const nestedErrors = buildNestedErrors(body.errors);
        setServerErrors(nestedErrors);
        valuesAtErrorRef.current = values;
        setErrors(nestedErrors);
        if (typeof nestedErrors.snapshot_archiving_config === "string") {
          setRepositoriesUpdatedAtError(allRepositories.dataUpdatedAt);
          allRepositories.refetch();
        }
        Object.keys(body.errors).sort((a, b) => a.split(".").length - b.split(".").length).forEach((field) => setFieldTouched(field, true, false));
      } else {
        UserNotification.error(
          t("archive_config_form.update_failed_text", { reason: (_b2 = body == null ? void 0 : body.message) != null ? _b2 : error }),
          t("archive_config_form.update_failed_title")
        );
      }
      setSubmitting(false);
    });
  };
  const saveConfiguration = (values, {
    setErrors,
    setSubmitting,
    setFieldTouched
  }) => {
    if (retentionTimeChanged) {
      setSubmitting(false);
      setShowConfirmDialog(true);
    } else {
      updateConfiguration(values, setErrors, setSubmitting, setFieldTouched);
    }
  };
  const abortSaving = () => {
    setShowConfirmDialog(false);
  };
  const sortedBackends = backends.backends.map((backend) => ({
    value: backend.id,
    label: `${backend.title} (${BackendTypes.getBackendType(backend.settings.type).label})`
  })).sort((a, b) => naturalSort(a.label.toLowerCase(), b.label.toLowerCase()));
  const retentionTimeString = (retentionTime) => {
    if (retentionTime === void 0 || Number(retentionTime) === 0) {
      return t("archive_config_form.unlimited");
    }
    return NumberUtils.formatNumber(retentionTime);
  };
  const currentBackend = (configuration) => backends.backends.find((backend) => backend.id === configuration.backend_id);
  const compressionTypesHelpText = /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "archive_config_form.compression_type_help",
      components: {
        docsLink: /* @__PURE__ */ React.createElement(DocumentationLink, { page: "setup-archiving#compression-type", text: t("archive_config_form.documentation") })
      }
    }
  ));
  const checksumTypesHelpText = /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "archive_config_form.checksum_type_help",
      components: {
        docsLink: /* @__PURE__ */ React.createElement(DocumentationLink, { page: "setup-archiving#checksum-type", text: t("archive_config_form.documentation") })
      }
    }
  ));
  const sortedCompressionTypes = [...CompressionTypes.compressionTypes].sort(
    (t1, t2) => naturalSort(t1.label.toLowerCase(), t2.label.toLowerCase())
  );
  const onCancel = () => history.push(ArchiveRoutes.LIST);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("archive_config_form.title")), /* @__PURE__ */ React.createElement(
    Formik,
    {
      innerRef: formikRef,
      initialValues: initialArchive,
      onSubmit: saveConfiguration,
      validate: (values) => {
        const errors = validateArchiveConfigForm(values);
        if (activeMode === "snapshot") {
          const snapshotErrors = validateSnapshotArchivingFields(values.snapshot_archiving_config);
          if (Object.keys(snapshotErrors).length > 0) {
            errors.snapshot_archiving_config = snapshotErrors;
          }
        }
        const snapshot = valuesAtErrorRef.current;
        if (snapshot) {
          Object.entries(serverErrors).forEach(([key, serverValue]) => {
            if (typeof serverValue === "string") {
              const expired = key === "snapshot_archiving_config" && repositoriesResolvedSinceError;
              if (!expired && values[key] === snapshot[key]) {
                errors[key] = serverValue;
              }
            } else if (typeof serverValue === "object" && serverValue !== null) {
              const groupUnchanged = Object.keys(serverValue).every(
                (childKey) => {
                  var _a2, _b2;
                  return ((_a2 = values[key]) == null ? void 0 : _a2[childKey]) === ((_b2 = snapshot[key]) == null ? void 0 : _b2[childKey]);
                }
              );
              if (groupUnchanged) {
                const existing = errors[key];
                errors[key] = typeof existing === "object" && existing !== null ? { ...existing, ...serverValue } : { ...serverValue };
              }
            }
          });
        }
        return errors;
      },
      validateOnMount: true
    },
    ({
      isSubmitting,
      isValidating,
      isValid,
      setFieldValue,
      values,
      setFieldTouched,
      setErrors,
      setSubmitting
    }) => {
      var _a2;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledForm, { className: "form form-horizontal" }, !canEdit && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "archive_config_form.read_only_alert",
          values: { permission: ARCHIVE_CONFIG_UPDATE_PERMISSION },
          components: { code: /* @__PURE__ */ React.createElement("code", null) }
        }
      )), /* @__PURE__ */ React.createElement("fieldset", { disabled: !canEdit }, isCloud && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        ReadOnlyFormGroup,
        {
          label: t("archive_config_form.backend_label"),
          value: (_a2 = currentBackend(values)) == null ? void 0 : _a2.title
        }
      ), /* @__PURE__ */ React.createElement(
        ReadOnlyFormGroup,
        {
          label: t("archive_config_form.retention_time_label"),
          value: initialArchive.retention_time
        }
      )), !isCloud && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { name: "backend_id" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          name: "backend_id",
          bsStyle: error ? "error" : void 0,
          error,
          label: t("archive_config_form.backend_label"),
          required: true,
          help: t("archive_config_form.backend_help"),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9"
        },
        /* @__PURE__ */ React.createElement(
          Select,
          {
            placeholder: t("archive_config_form.select_backend"),
            disabled: !canEdit,
            clearable: false,
            options: sortedBackends,
            onChange: (selected) => onChange({ target: { value: selected, name } }),
            value
          }
        )
      )), isSnapshotEnabled && /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, t("archive_config_form.archiving_strategy")), /* @__PURE__ */ React.createElement("div", { className: "col-sm-9" }, /* @__PURE__ */ React.createElement(
        SegmentedControl,
        {
          data: [
            {
              value: "snapshot",
              label: t("archive_config_form.snapshot_strategy"),
              disabled: !isVersionSupported
            },
            { value: "default", label: t("archive_config_form.scan_strategy") }
          ],
          value: activeMode,
          onChange: (val) => {
            setUserSelectedMode(val);
            setServerErrors({});
            valuesAtErrorRef.current = null;
            setRepositoriesUpdatedAtError(null);
            setErrors({});
            if (val === "snapshot" && !values.snapshot_archiving_config) {
              setFieldValue("snapshot_archiving_config", DEFAULT_SNAPSHOT_CONFIG);
            }
          }
        }
      ), /* @__PURE__ */ React.createElement(InputDescription, { help: t("archive_config_form.strategy_help") }), activeMode === "default" && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, t("archive_config_form.scan_strategy_warning")), !isVersionSupported && /* @__PURE__ */ React.createElement("p", { className: "help-block" }, t("archive_config_form.snapshot_requires_opensearch")))), activeMode === "snapshot" ? /* @__PURE__ */ React.createElement(
        SnapshotArchivingFields,
        {
          repositories: repositoryEntries,
          credentialProblemRepositories,
          hasSupportedRepositories: supportedRepositories.length > 0,
          isLoadingRepositories: allRepositories.isLoading,
          disabled: !canEdit
        }
      ) : /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, t("archive_config_form.enable_multithreading")), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          name: "parallelize_archive_creation",
          id: "parallelize_archive_creation",
          formGroupClassName: "col-sm-9",
          label: t("archive_config_form.multithreading_label"),
          type: "checkbox",
          help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
            Trans,
            {
              t,
              i18nKey: "archive_config_form.multithreading_help",
              components: { lineBreak: /* @__PURE__ */ React.createElement("br", null), strong: /* @__PURE__ */ React.createElement("strong", null), paragraph: /* @__PURE__ */ React.createElement("p", null) }
            }
          ))
        }
      )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, t("archive_config_form.restrict_leader_label")), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          name: "restrict_to_leader",
          id: "restrict_to_leader",
          type: "checkbox",
          formGroupClassName: "col-sm-9",
          onChange: () => handleRestrictToLeaderChange(setFieldValue, values, setFieldTouched),
          label: t("archive_config_form.restrict_leader_checkbox"),
          help: t("archive_config_form.restrict_leader_help", { product_name: productName })
        }
      )), !values.restrict_to_leader && /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "parallel_restore_jobs",
          id: "parallel_restore_jobs",
          label: t("archive_config_form.parallel_restore_jobs_label"),
          min: 1,
          help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null, t("archive_config_form.parallel_restore_jobs_help")), " ", /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(
            Trans,
            {
              t,
              i18nKey: "archive_config_form.parallel_restore_jobs_beta",
              components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
            }
          ))),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "max_segment_size",
          id: "max_segment_size",
          label: t("archive_config_form.max_segment_size_label"),
          min: 1,
          help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
            Trans,
            {
              t,
              i18nKey: "archive_config_form.max_segment_size_help",
              components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
            }
          )),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      ), /* @__PURE__ */ React.createElement(Field, { name: "segment_compression_type" }, ({ field: { name, value, onChange }, meta: { error } }) => {
        var _a3;
        return /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            error,
            name: "segment_compression_type",
            label: t("archive_config_form.compression_type_label"),
            help: compressionTypesHelpText,
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              placeholder: t("archive_config_form.select_compression_type"),
              disabled: !canEdit,
              options: sortedCompressionTypes,
              onChange: (selected) => onChange({ target: { value: selected, name } }),
              value: (_a3 = CompressionTypes.getCompressionType(value)) == null ? void 0 : _a3.value,
              clearable: false
            }
          )
        );
      }), /* @__PURE__ */ React.createElement(Field, { name: "segment_checksum_type" }, ({ field: { name, value, onChange }, meta: { error } }) => {
        var _a3;
        return /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            error,
            name: "segment_checksum_type",
            label: t("archive_config_form.checksum_type_label"),
            help: checksumTypesHelpText,
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              placeholder: t("archive_config_form.select_checksum_type"),
              disabled: !canEdit,
              options: [...ChecksumTypes.checksumTypes],
              onChange: (selected) => onChange({ target: { value: selected, name } }),
              value: (_a3 = ChecksumTypes.getChecksumType(value)) == null ? void 0 : _a3.value,
              clearable: false
            }
          )
        );
      }), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "restore_index_batch_size",
          id: "restore_index_batch_size",
          label: t("archive_config_form.restore_batch_size_label"),
          help: t("archive_config_form.restore_batch_size_help"),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          min: 1,
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "archive_failure_threshold",
          id: "archive_failure_threshold",
          label: t("archive_config_form.failure_threshold_label"),
          help: t("archive_config_form.failure_threshold_help"),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          min: 1,
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          min: 0,
          name: "retention_time",
          id: "retention_time",
          label: t("archive_config_form.retention_time_label"),
          onChange: ({ target: { value } }) => {
            setRetentionTimeChanged(initialArchive.retention_time !== Number(value));
            setFieldValue("retention_time", value);
          },
          help: t("archive_config_form.retention_time_help"),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      )), /* @__PURE__ */ React.createElement(Field, { name: "excluded_streams" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          bsStyle: error ? "error" : void 0,
          error,
          name: "excluded_streams",
          label: t("archive_config_form.streams_label"),
          help: t("archive_config_form.streams_help"),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9"
        },
        /* @__PURE__ */ React.createElement(
          ArchiveConfigStreamSelectionForm,
          {
            excludedStreams: value,
            updateExcludedStreams: (selected) => onChange({ target: { value: selected, name } })
          }
        )
      )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { smOffset: 3, sm: 9 }, canEdit && /* @__PURE__ */ React.createElement(
        FormSubmit,
        {
          submitButtonText: t("archive_config_form.update_configuration"),
          disabledSubmit: isValidating || !isValid || snapshotBlocked(values),
          isSubmitting,
          isAsyncSubmit: true,
          submitLoadingText: t("archive_config_form.updating_configuration"),
          onCancel
        }
      ))))), /* @__PURE__ */ React.createElement(
        ConfirmDialog,
        {
          show: showConfirmDialog,
          onConfirm: () => {
            setShowConfirmDialog(false);
            updateConfiguration(values, setErrors, setSubmitting, setFieldTouched);
          },
          onCancel: abortSaving,
          title: t("archive_config_form.confirm_title")
        },
        /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("archive_config_form.retention_confirm_text", {
          new_retention: retentionTimeString(values == null ? void 0 : values.retention_time),
          current_retention: retentionTimeString(initialArchive == null ? void 0 : initialArchive.retention_time)
        })), /* @__PURE__ */ React.createElement("p", null, t("archive_config_form.deleted_not_restorable")), /* @__PURE__ */ React.createElement("p", null, t("archive_config_form.continue_question")))
      ));
    }
  ));
};
export default ArchiveConfigForm;
